import { RefObject } from "react";
import {
  type AdvancedChipOption,
  type ChipOptionValue,
  REFERENCE_MENTION_CONFIG,
} from "./chipOptionsConfig";

function findChipValueById(
  groups: AdvancedChipOption[] | undefined,
  prefix: string,
  id: string
): ChipOptionValue | undefined {
  if (!groups) return undefined;
  const group = groups.find((g) => (g.prefix || "") === prefix);
  if (!group) return undefined;
  return group.values.find((v) => {
    const vId = typeof v === "string" ? v : v.value;
    return vId === id;
  });
}

function getMentionToken(
  chip: string,
  prefix?: string,
  noSpaceOnInsert?: boolean
): string {
  const token = prefix ? `((${prefix}${chip}))` : `((${chip}))`;
  return `${token}${noSpaceOnInsert ? "" : " "}`;
}

function computeTextWithInsertion(
  currentText: string,
  token: string,
  textarea: HTMLTextAreaElement | null
): { nextText: string; caretIndex: number } {
  if (
    textarea &&
    typeof textarea.selectionStart === "number" &&
    typeof textarea.selectionEnd === "number"
  ) {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const nextText =
      currentText.slice(0, start) + token + currentText.slice(end);
    const caretIndex = start + token.length;
    return { nextText, caretIndex };
  }
  const nextText = (currentText || "") + token;
  return { nextText, caretIndex: nextText.length };
}

export function handleChipClickRaw(
  chip: string,
  options: {
    textareaRef: RefObject<HTMLTextAreaElement | null>;
    currentText: string;
    noSpaceOnInsert?: boolean;
    setText: (next: string) => void;
    prefix?: string;
  }
) {
  const token = getMentionToken(chip, options.prefix, options.noSpaceOnInsert);
  const { nextText, caretIndex } = computeTextWithInsertion(
    options.currentText,
    token,
    options.textareaRef.current
  );
  options.setText(nextText);
  setTimeout(() => {
    const ta = options.textareaRef.current;
    if (ta) {
      ta.focus();
      ta.setSelectionRange(caretIndex, caretIndex);
    }
  }, 0);
}

export function injectMentionsIntoDoc(
  node: any,
  groups?: AdvancedChipOption[]
): any {
  if (!node) return node;
  if (node.type === "codeBlock") return node;
  if (Array.isArray(node))
    return node.map((n) => injectMentionsIntoDoc(n, groups));

  if (node.content && Array.isArray(node.content)) {
    const nextContent: any[] = [];
    node.content.forEach((child: any) => {
      if (child.type === "text" && typeof child.text === "string") {
        const parts: string[] = child.text.split(/(\(\([^)]+\)\))/g);
        parts.forEach((part: string) => {
          if (part.length === 0) return;
          const mentionMatch = part.match(/^\(\(([^a-zA-Z0-9\s]?)(.+)\)\)$/);
          if (mentionMatch) {
            const prefixChar = mentionMatch[1] || "";
            const id = mentionMatch[2];
            const group = groups?.find((g) => (g.prefix || "") === prefixChar);
            const isReference = prefixChar === REFERENCE_MENTION_CONFIG.prefix;
            // Look up the chip's display metadata so the rehydrated mention
            // shows the friendly label and source avatar — without this, an
            // id like `<uuid>.output` renders as its raw value in the chip.
            const entry = findChipValueById(groups, prefixChar, id);
            const entryMeta = entry && typeof entry !== "string" ? entry : null;
            // References render as regular `mention` nodes carrying the reference
            // colour + prefix — the CustomMention extension reads `attrs.color` to
            // paint fuchsia styling. A distinct `referenceMention` node type is
            // NOT registered in the TipTap schema, so emitting one silently drops
            // the node on render.
            nextContent.push({
              type: "mention",
              attrs: {
                id,
                mentionSuggestionChar: isReference
                  ? REFERENCE_MENTION_CONFIG.prefix || "@"
                  : "/",
                prefix: prefixChar,
                icon: prefixChar,
                color:
                  group?.color ||
                  (isReference ? REFERENCE_MENTION_CONFIG.color : "default"),
                label: entryMeta?.label ?? null,
                nodeImage: entryMeta?.sourceImage ?? null,
                nodeColor: entryMeta?.sourceColor ?? null,
                nodeName: entryMeta?.sourceName ?? null,
                nodeKind: entryMeta?.sourceKind ?? null,
              },
            });
          } else {
            nextContent.push({
              type: "text",
              text: part,
              marks: child.marks,
            });
          }
        });
      } else if (child && typeof child === "object") {
        nextContent.push(injectMentionsIntoDoc(child, groups));
      } else {
        nextContent.push(child);
      }
    });
    return { ...node, content: nextContent };
  }
  return node;
}
